import {
  RCPS_SECTIONS,
  STOCK_OPTION_SECTIONS,
  type RcpsSection,
} from "./fields";
import { VOLATILITY_PERIODS, volatilityWindow } from "./volatility";
export type Tool = "rcps" | "bdt" | "so" | "volatility" | "bootstrapping";
export type Params = Record<string, string>;
export type Curve = { periods: number[]; rates: string[] };
export type Inputs = {
  params: Params;
  rf: Curve;
  kd: Curve;
  peers: { symbol: string; name: string }[];
};
export type Evaluation = {
  kind: Tool;
  inputs: Inputs;
  payload: Record<string, unknown>;
  result: unknown;
  calculated: string;
};
export type SavedEvaluation<T = Evaluation> = {
  id: string;
  name: string;
  updated: string;
  payload: T;
};
const section = (id: string) => RCPS_SECTIONS.find((s) => s.id === id)!;
const redemption: RcpsSection = {
  ...section("redemption"),
  rows: [
    ...section("redemption").rows,
    [
      {
        key: "redemption_claim_interest_calculation_method",
        label: "상환청구이자 계산방법",
        type: "select",
        options: ["복리", "단리"],
      },
      { key: "redemption_cycle", label: "상환청구 주기", type: "number" },
      {
        key: "redemption_cycle_interval",
        label: "상환청구 주기 단위",
        type: "select",
        options: [
          { value: "day", label: "일" },
          { value: "month", label: "월" },
          { value: "year", label: "년" },
        ],
      },
    ],
  ],
};
export function sectionsFor(kind: Tool): RcpsSection[] {
  if (kind === "rcps")
    return RCPS_SECTIONS.map((s) =>
      s.id === "redemption"
        ? redemption
        : s.id === "stepup"
          ? {
              ...s,
              rows: s.rows.map((row) =>
                row.map((f) =>
                  f.key === "coupon_schedule_method"
                    ? f
                    : {
                        ...f,
                        showIf: (p: Params) =>
                          f.key.startsWith("coupon_schedule_")
                            ? p.coupon_schedule_method === "explicit"
                            : p.coupon_schedule_method === "stepup",
                      },
                ),
              ),
            }
          : s,
    );
  if (kind === "so") return STOCK_OPTION_SECTIONS;
  if (kind === "bdt")
    return [
      section("company"),
      section("node"),
      {
        ...section("basic"),
        rows: section("basic")
          .rows.map((row) =>
            row.filter(
              (f) =>
                ![
                  "current_stock_price",
                  "dividend_yield",
                  "conversion_price",
                ].includes(f.key),
            ),
          )
          .filter((row) => row.length),
      },
      {
        id: "volatility",
        step: 4,
        title: "이자율 변동성",
        rows: [
          [
            {
              key: "volatility_input",
              label: "변동성 입력방법",
              type: "select",
              options: ["직접입력", "자동계산"],
            },
            {
              key: "interest_volatility",
              label: "이자율 변동성",
              type: "number",
              unit: "%",
              required: true,
              showIf: (p) => p.volatility_input === "직접입력",
            },
          ],
          [
            {
              key: "volatility_date_from",
              label: "변동성 계산 시작일",
              type: "date",
              showIf: (p) => p.volatility_input === "자동계산",
            },
            {
              key: "volatility_business_days",
              label: "연간 영업일수",
              type: "number",
            },
            {
              key: "volatility_standard",
              label: "변동성 기준",
              type: "select",
              options: [
                "AAA",
                "AA+",
                "AA0",
                "AA-",
                "A+",
                "A0",
                "A-",
                "BBB+",
                "BBB0",
                "BBB-",
              ],
            },
          ],
        ],
      },
      redemption,
      section("bootstrapping"),
    ];
  if (kind === "volatility")
    return [
      {
        id: "volatility",
        step: 1,
        title: "주가 변동성 산출조건",
        rows: [
          [
            {
              key: "valuation_date",
              label: "평가기준일",
              type: "date",
              required: true,
            },
            {
              key: "volatility_conversion_days",
              label: "변동성 연환산 적용일",
              type: "number",
              unit: "일",
              required: true,
            },
          ],
          [
            {
              key: "volatility_period",
              label: "주가 조회기간",
              type: "select",
              options: VOLATILITY_PERIODS,
            },
            {
              key: "volatility_date_from",
              label: "주가 조회 시작일",
              type: "date",
              required: true,
              showIf: (p) =>
                !p.volatility_period || p.volatility_period === "custom",
            },
            {
              key: "stockprice_query_period",
              label: "주가 관측수",
              type: "number",
              unit: "개",
              required: true,
              showIf: (p) => p.volatility_period === "observations",
            },
          ],
        ],
      },
    ];
  return [
    {
      id: "date",
      step: 1,
      title: "평가기준일",
      rows: [
        [
          {
            key: "valuation_date",
            label: "평가기준일",
            type: "date",
            required: true,
          },
        ],
      ],
    },
    section("bootstrapping"),
  ];
}
export function initialInputs(kind: Tool): Inputs {
  const params: Params = {
    valuation_model: kind === "bdt" ? "BDT" : "TF",
    date_array_method: "node",
    node_count: "201",
    node_interval: "7",
    dividend_yield: "0",
    is_nominal_interest: "0",
    nominal_interest_rate: "0",
    nominal_interest_cycle: "1",
    nominal_interest_cycle_interval: "month",
    conversion_cycle: "0",
    conversion_cycle_interval: "day",
    redemption_cycle: "0",
    redemption_cycle_interval: "day",
    redemption_claim_interest_rate: "0",
    redemption_claim_interest_calculation_method: "복리",
    volatility_input: "직접입력",
    volatility_conversion_days: "248",
    stockprice_query_period: "365",
    interest_input_method: "자동입력",
    rate_source: "seibro",
    credit_rate: "AA+",
    is_public_bond: "공모",
    rf_basis: "0.25",
    kd_basis: "0.25",
    volatility_standard: "AAA",
    volatility_business_days: "245",
    coupon_schedule_method: "flat",
    calloption_ytc_method: "flat",
    stepup_annual_period_months: "12",
    valuation_date: new Date().toLocaleDateString("en-CA"),
  };
  for (const s of sectionsFor(kind)) {
    if (s.toggleKey) params[s.toggleKey] = "0";
    for (const f of s.rows.flat())
      if (f.options?.length && params[f.key] === undefined) {
        const first = f.options[0];
        params[f.key] = typeof first === "string" ? first : first.value;
      }
  }
  Object.assign(params, {
    is_conversion_prevention: "0", // 발행자 콜옵션 전환방지조항 — 기본 미반영(API·애드인과 같게)
    call_exercise_ratio: "100",
    ipo_refixing_cycle: "1",
    stockprice_refixing_cycle: "3",
    stockprice_refixing_base_1: "1",
    stockprice_refixing_base_2: "1",
    stockprice_refixing_base_3: "4",
  });
  params.volatility_period = "1y";
  if (kind === "volatility") {
    params.stockprice_query_period = "180";
  }
  const curve = () => ({
    periods: [0.25, 0.5, 0.75, 1, 3, 5, 10, 20],
    rates: Array<string>(8).fill(""),
  });
  return { params, rf: curve(), kd: curve(), peers: [] };
}
const numericSelects = new Set([
  "rf_basis",
  "kd_basis",
  "is_nominal_interest",
  "is_upward_refixing",
  "is_conversion_prevention",
  "calloption_interest_payment_cycle",
]);
export function validateCurve(curve: Curve, label: string) {
  if (curve.periods.length < 2 || curve.periods.length !== curve.rates.length)
    throw new Error(`${label}: 만기와 이자율을 두 구간 이상 입력해 주세요.`);
  curve.periods.forEach((period, i) => {
    if (
      !Number.isFinite(period) ||
      period <= 0 ||
      period > 100 ||
      (i > 0 && period <= curve.periods[i - 1])
    )
      throw new Error(
        `${label}: 만기는 0 초과 100년 이하의 오름차순으로 입력해 주세요.`,
      );
    if (
      curve.rates[i].trim() === "" ||
      !Number.isFinite(Number(curve.rates[i])) ||
      Number(curve.rates[i]) <= -100
    )
      throw new Error(`${label}: 모든 만기의 이자율을 확인해 주세요.`);
  });
}
export function buildPayload(
  kind: Tool,
  inputs: Inputs,
): Record<string, unknown> {
  const { params: p, rf, kd, peers } = inputs;
  const payload: Record<string, unknown> = {};
  for (const s of sectionsFor(kind)) {
    if (s.toggleKey) {
      payload[s.toggleKey] = Number(p[s.toggleKey] || 0);
      if (p[s.toggleKey] !== "1") continue;
    }
    for (const f of s.rows.flat()) {
      if (f.showIf && !f.showIf(p)) continue;
      if (f.lock?.(p)) continue; // 고정 칸(제3자 전환방지조항 등)은 보내지 않는다 — API 기본값 사용
      if (f.key === "volatility_period") continue;
      const value = (p[f.key] || "").trim();
      if (
        f.required &&
        !value &&
        !(f.key === "volatility" && p.volatility_input !== "직접입력")
      )
        throw new Error(`${f.label}을(를) 입력해 주세요.`);
      if (!value) {
        if (f.key === "volatility" && p.volatility_input === "직접입력")
          throw new Error("연 변동성을 입력해 주세요.");
        continue;
      }
      if (
        f.type === "number" ||
        f.type === "bool" ||
        numericSelects.has(f.key)
      ) {
        const n = Number(value.replace(/,/g, ""));
        if (!Number.isFinite(n))
          throw new Error(`${f.label}을(를) 확인해 주세요.`);
        // 숫자 칸은 모두 0 이상(주기·가격·주식수·비율) — 입력칸이 '-'를 막지만 저장본·샘플도 여기서 거른다
        if (f.type === "number" && n < 0)
          throw new Error(`${f.label}은(는) 0 이상이어야 합니다.`);
        payload[f.key] = f.unit === "%" ? n / 100 : n;
      } else payload[f.key] = value;
    }
  }
  for (const k of [
    "stepup_annual_increment_schedule",
    "coupon_schedule_rates",
    "coupon_schedule_dates",
  ])
    if (payload[k]) {
      payload[k] = p[k]
        .split(",")
        .map((v) =>
          k === "coupon_schedule_dates" ? v.trim() : Number(v.trim()) / 100,
        );
      if (
        k !== "coupon_schedule_dates" &&
        (payload[k] as number[]).some((v) => !Number.isFinite(v))
      )
        throw new Error("쿠폰 이자율 목록을 확인해 주세요.");
    }
  if (kind === "rcps") {
    if (p.coupon_schedule_method === "stepup" && !p.stepup_first_date)
      throw new Error("최초 이자율조정일을 입력해 주세요.");
    if (p.coupon_schedule_method === "explicit") {
      const dates = payload.coupon_schedule_dates as string[] | undefined;
      const rates = payload.coupon_schedule_rates as number[] | undefined;
      if (
        !dates?.length ||
        dates.length !== rates?.length ||
        dates.some(
          (d, i) =>
            !/^\d{4}-\d{2}-\d{2}$/.test(d) || (i > 0 && d <= dates[i - 1]),
        )
      )
        throw new Error(
          "쿠폰 적용일과 이자율 개수를 맞추고 날짜를 오름차순으로 입력해 주세요.",
        );
    }
  }
  for (const [flag, keys] of [
    ["is_diluted", ["outstanding_shares", "conversion_shares"]],
    [
      "is_ipo_refixing",
      [
        "ipo_refixing_start_date",
        "ipo_refixing_end_date",
        "ipo_refixing_ratio",
        "ipo_refixing_cycle",
      ],
    ],
    [
      "is_stockprice_refixing",
      [
        "stockprice_refixing_start_date",
        "stockprice_refixing_end_date",
        "stockprice_refixing_minimum_conversion_price",
        "stockprice_refixing_cycle",
      ],
    ],
    [
      "is_calloption",
      [
        "calloption_start_date",
        "calloption_end_date",
        "calloption_interest_rate",
        "call_exercise_ratio",
      ],
    ],
  ] as [string, string[]][])
    if (p[flag] === "1" && keys.some((k) => !(p[k] || "").trim()))
      throw new Error("반영한 추가 조건의 날짜와 수치를 모두 입력해 주세요.");
  if (
    kind === "bdt" &&
    p.volatility_input === "자동계산" &&
    (!p.volatility_date_from || p.volatility_date_from >= p.valuation_date)
  )
    throw new Error("이자율 변동성 계산 시작일을 확인해 주세요.");
  for (const k of [
    "volatility",
    "interest_volatility",
    "dividend_yield",
    "redemption_claim_interest_rate",
  ])
    if (payload[k] !== undefined && Number(payload[k]) < 0)
      throw new Error("변동성·배당률·상환이자율은 음수를 입력할 수 없습니다.");
  if (["rcps", "so", "bdt"].includes(kind)) {
    if (p.issue_date > p.valuation_date || p.valuation_date >= p.maturity_date)
      throw new Error("발행일 ≤ 평가기준일 < 만기일이어야 합니다.");
    if (
      Number(p.issue_price) <= 0 ||
      (kind !== "bdt" &&
        (Number(p.current_stock_price) <= 0 || Number(p.conversion_price) <= 0))
    )
      throw new Error("가격은 0보다 큰 값을 입력해 주세요.");
    if (
      p.date_array_method === "node" &&
      (!Number.isInteger(Number(p.node_count)) ||
        Number(p.node_count) < 2 ||
        Number(p.node_count) > (kind === "bdt" ? 2000 : 800))
    )
      throw new Error(
        kind === "bdt" ? "노드개수는 2~2,000 사이의 정수로 입력해 주세요." : "노드개수는 2~800 사이의 정수로 입력해 주세요.",
      );
    if (
      p.date_array_method === "interval" &&
      (!Number.isInteger(Number(p.node_interval)) ||
        Number(p.node_interval) < 1)
    )
      throw new Error("노드간격은 1일 이상으로 입력해 주세요.");
    for (const [start, end] of [
      ["conversion_start_date", "conversion_end_date"],
      ["redemption_start_date_contract", "redemption_end_date"],
      [
        "redemption_start_date_dividend_eligible_earnings",
        "redemption_end_date",
      ],
      ["ipo_refixing_start_date", "ipo_refixing_end_date"],
      ["stockprice_refixing_start_date", "stockprice_refixing_end_date"],
      ["calloption_start_date", "calloption_end_date"],
    ]) {
      if (
        payload[start] &&
        payload[end] &&
        (String(payload[start]) > String(payload[end]) ||
          String(payload[end]) > p.maturity_date)
      )
        throw new Error(
          "청구·행사 기간의 시작일, 종료일과 만기일을 확인해 주세요.",
        );
    }
    payload.node_interval = Number(p.node_interval || 0);
    payload.node_count = Number(p.node_count || 0);
    if (kind === "bdt") payload.valuation_model = "BDT";
  }
  if (
    kind === "volatility" ||
    (kind !== "bdt" && p.volatility_input === "유사회사선정")
  ) {
    if (!peers.length) throw new Error("유사회사를 한 곳 이상 선택해 주세요.");
    payload.peer_symbol_list = peers.map((x) => x.symbol);
    payload.peer_company_list = peers.map((x) => x.name);
    payload.volatility_date_to = p.valuation_date;
    // 스톡옵션 API 는 유사회사선정이어도 volatility 칸을 필수로 받는다(값은 쓰지 않고 유사회사 변동성으로 계산 — 실측).
    // 없으면 422 로 계산이 막혔다.
    if (kind === "so" && payload.volatility === undefined) payload.volatility = 0;
    // 주가 조회기간 — 변동성 계산 화면과 같은 방식. 복합상품·스톡옵션의 예전 저장본은 관측수(N거래일)만 있었다.
    // (복합상품·스톡옵션은 예전에 시작일을 쓰지 않았다 — 샘플 입력의 시작일은 옵션부사채용이라 기간 판단에 쓰지 않는다)
    const mode = p.volatility_period || (kind === "volatility" ? "custom" : "observations");
    delete payload.volatility_date_from;
    delete payload.stockprice_query_period;
    Object.assign(payload, volatilityWindow({ ...p, volatility_period: mode, stockprice_query_period: p.stockprice_query_period || "365" }));
    if (kind === "volatility" && !Number.isInteger(Number(p.volatility_conversion_days)))
      throw new Error("연환산 적용일은 양의 정수로 입력해 주세요.");
    if (Number(p.volatility_conversion_days) <= 0)
      throw new Error("연환산 적용일은 0보다 커야 합니다.");
  }
  if (kind !== "volatility") {
    if (p.interest_input_method === "직접입력") {
      validateCurve(rf, "Rf");
      if (kind !== "so") validateCurve(kd, "Kd");
    }
    payload.rf_ytm_periods = rf.periods;
    payload.rf_ytms = rf.rates.map((v) => Number(v) / 100);
    payload.kd_ytm_periods = kd.periods;
    payload.kd_ytms = kd.rates.map((v) => Number(v) / 100);
  }
  return payload;
}
export function demoInputs(kind: Tool): Inputs {
  const v = initialInputs(kind);
  Object.assign(v.params, {
    valuation_company: `샘플 · ${kind.toUpperCase()}`,
    valuation_date: "2025-12-31",
    issue_date: "2025-01-01",
    maturity_date: "2030-01-01",
    current_stock_price: "10000",
    issue_price: "10000",
    conversion_price: "12000",
    conversion_start_date: "2026-01-01",
    conversion_end_date: "2030-01-01",
    redemption_start_date_contract: "2027-01-01",
    redemption_start_date_dividend_eligible_earnings: "2027-01-01",
    redemption_end_date: "2030-01-01",
    redemption_claim_interest_rate: "5",
    volatility: "30",
    interest_volatility: "15",
    volatility_date_from: "2024-12-31",
    interest_input_method: "직접입력",
  });
  v.rf.rates = ["2.4", "2.45", "2.5", "2.55", "2.7", "2.9", "3.1", "3.2"];
  v.kd.rates = ["3.4", "3.45", "3.5", "3.6", "3.8", "4.1", "4.4", "4.6"];
  v.peers = [
    { symbol: "KRX:005930", name: "삼성전자" },
    { symbol: "KRX:000660", name: "SK하이닉스" },
  ];
  return v;
}
